import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY, Observable, of } from 'rxjs';
import { StructureList } from '../shared/models/structure-list';
import { StructuredInfoListDto } from '../shared/dto/structured-info-list-dto';
import { catchError, map, tap } from 'rxjs/operators';
import { StructureInfoFactory } from '../shared/helpers/structure-info.factory';
import { AppUrls } from '../route/app-urls';
import { SearchOptions } from './search-options';
import { SearchDefinitionFactory } from './search-definition.factory';
import { SearchDefinition } from './search-definition';
import { SearchDefinitionDto } from './search-definition-dto';
import { ErrorHandlers } from '../shared/helpers/error-handlers';
import { SearchFormParams } from './search-form-params';
import { RBCheck } from '@ldw/roesberg';
import { SettingsService } from '../shared/settings/settings.service';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private http = inject(HttpClient);
  private settings = inject(SettingsService);

  private readonly searchCache: {
    [wdId: string]: SearchFormParams;
  } = {};

  private getFormParams(wdId: string): SearchFormParams {
    if (!this.searchCache[wdId]) {
      this.searchCache[wdId] = new SearchFormParams();
    }
    return this.searchCache[wdId];
  }

  public getSearchFields(wdId: string): Observable<SearchDefinition> {
    const searchFormParams: SearchFormParams = this.getFormParams(wdId);
    if (searchFormParams.searchFields) {
      return of(searchFormParams.searchFields);
    }

    return this.http
      .get<SearchDefinitionDto>(AppUrls.getApiUrl(AppUrls.urlSearch, 'getfilters' + `?webDokuId=${wdId}`))
      .pipe(
        map((dtoRaw) => SearchDefinitionFactory.fromDTO(dtoRaw, this.settings.settings.searchSettings)),
        tap((searchDefinition) => {
          searchFormParams.searchFields = searchDefinition;
        }),
        catchError((error) => {
          return ErrorHandlers.errorHandler(error);
        })
      );
  }

  public getSearchValues(wdId: string, fieldname: string): Observable<string[]> {
    const searchFormParams: SearchFormParams = this.getFormParams(wdId);

    if (searchFormParams.filterValues.has(fieldname)) {
      const list = searchFormParams.filterValues.get(fieldname);
      if (list == undefined) {
        return of([]);
      }
      return of(list);
    }

    return this.http
      .get<string[]>(
        AppUrls.getApiUrl(AppUrls.urlSearch, 'getfiltervalues') + `?webDokuId=${wdId}` + '&fieldname=' + fieldname
      )
      .pipe(
        map((dtoRaw) => {
          searchFormParams.UpdateValueCache(fieldname, dtoRaw);
          return dtoRaw;
        }),
        catchError((error) => {
          return ErrorHandlers.errorHandler(error);
        })
      );
  }

  public search(options: SearchOptions): Observable<StructureList> {
    return this.http.post<StructuredInfoListDto>(AppUrls.getApiUrl(AppUrls.urlSearch), JSON.stringify(options)).pipe(
      //retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
      map((dtoRaw) => {
        return StructureInfoFactory.fromStructureDTOList(dtoRaw, options.webDokuId);
      }),
      catchError((error) => {
        return ErrorHandlers.errorHandler(error);
      })
    );
  }

  public searchNodesByName(apiUrl: string, options: SearchOptions): Observable<StructureList> {
    const name = options?.getSearchText();
    if (RBCheck.isNullOrEmpty(name)) {
      return of(new StructureList());
    }

    return this.http
      .post<StructuredInfoListDto>(
        AppUrls.getApiUrl(apiUrl, 'GetMatchingNodes'),
        JSON.stringify(options)
      )
      .pipe(
        //retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
        map((dtoRaw) => {
          return StructureInfoFactory.fromStructureDTOList(dtoRaw, options.webDokuId);
        }),
        catchError((error) => {
          return ErrorHandlers.errorHandler(error);
        })
      );
  }
}
